import { isTelegramWebApp } from './platform';

const FALLBACK_SUPPORT_TELEGRAM =
  String(import.meta.env.VITE_SUPPORT_TELEGRAM || '').trim() || 'https://t.me/cozzysim_support';

/** Normalize @handle / t.me/... / full URL into https://t.me/... */
export function toTelegramSupportUrl(raw?: string | null): string {
  const value = String(raw || '').trim();
  if (!value) return FALLBACK_SUPPORT_TELEGRAM;

  if (/^https?:\/\//i.test(value)) return value;
  if (/^tg:\/\//i.test(value)) return value;
  if (/^t\.me\//i.test(value)) return `https://${value}`;

  const handle = value.replace(/^@/, '').replace(/^https?:\/\/t\.me\//i, '');
  if (!handle) return FALLBACK_SUPPORT_TELEGRAM;
  return `https://t.me/${handle}`;
}

export function telegramSupportHandle(raw?: string | null): string {
  const url = toTelegramSupportUrl(raw);
  try {
    const path = new URL(url).pathname.replace(/^\//, '');
    return path ? `@${path.split('/')[0]}` : '@support';
  } catch {
    return '@support';
  }
}

/**
 * `openTelegramLink('https://t.me/bot')` silently does nothing if the user has
 * never pressed Start on that bot. `?start=` always opens the Start screen.
 */
export function withTelegramBotStart(url: string): string {
  try {
    const parsed = new URL(url);
    if (!/(^|\.)t\.me$/i.test(parsed.hostname)) return url;
    const parts = parsed.pathname.replace(/^\//, '').split('/').filter(Boolean);
    // Mini App deep links (t.me/bot/app) must keep startapp, not /start.
    if (parts.length >= 2) return url;
    if (!parsed.searchParams.has('start') && !parsed.searchParams.has('startapp')) {
      parsed.searchParams.set('start', 'chat');
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

/** Open Support Bot inside Telegram Mini App (or browser tab on web). */
export function openTelegramSupport(raw?: string | null): boolean {
  const url = withTelegramBotStart(toTelegramSupportUrl(raw));
  if (!url) return false;

  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  if (tg && typeof tg.openTelegramLink === 'function' && /^https:\/\/t\.me\//i.test(url)) {
    try {
      tg.openTelegramLink(url);
      return true;
    } catch {
      /* fall through */
    }
  }

  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
}

export function shouldOpenTelegramSupportInApp(): boolean {
  return isTelegramWebApp();
}
